import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Facebook, Youtube, AtSign, ArrowUpRight, Play } from "lucide-react";
import { SOCIALS, PHOTOS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.3 0 .58.05.85.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIAL_BTNS = [
  { label: "Instagram", href: SOCIALS.instagram, Icon: Instagram },
  { label: "Facebook", href: SOCIALS.facebook, Icon: Facebook },
  { label: "TikTok", href: SOCIALS.tiktok, Icon: TikTokIcon },
  { label: "YouTube", href: SOCIALS.youtube, Icon: Youtube },
  { label: "Threads", href: SOCIALS.threads, Icon: AtSign },
];

export default function SocialStar() {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    fetch(`${API}/instagram/feed?limit=9`)
      .then((r) => r.json())
      .then((d) => setFeed(d))
      .catch(() => setFeed({ connected: false, data: [] }));
  }, []);

  const livePosts = feed?.connected && feed?.data?.length > 0;

  return (
    <section id="social" data-testid="social-section" className="relative overflow-hidden py-28 lg:py-40">
      <motion.div
        className="pointer-events-none absolute -right-40 top-20 h-[26rem] w-[26rem] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(197,160,89,0.14), transparent 65%)" }}
        animate={{ scale: [1, 1.18, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-body text-[0.65rem] font-medium uppercase tracking-[0.5em] text-[#C5A059]"
        >
          Follow the Movement
        </motion.p>
        <div className="mt-6 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.76, 0, 0.24, 1] }}
            className="font-display text-4xl font-light leading-tight tracking-tighter text-[#6B5D4F] sm:text-5xl lg:text-6xl"
          >
            <span className="italic text-[#C5A059]">@susangrauofficial</span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="max-w-md"
          >
            <p className="font-body text-base font-light leading-relaxed text-[#8B7D6B]">
              A global community of 160,000+ followers — daily hope, healing, and infinite wisdom,
              straight from Susan.
            </p>
            {livePosts && (
              <p data-testid="ig-live-badge" className="mt-3 flex items-center gap-2 font-body text-[0.6rem] font-medium uppercase tracking-[0.3em] text-[#C5A059]">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#C5A059]" aria-hidden="true" />
                Live — latest posts update automatically
              </p>
            )}
          </motion.div>
        </div>

        {livePosts ? (
          <div data-testid="ig-live-grid" className="mt-16 grid grid-cols-2 gap-4 md:grid-cols-3">
            {feed.data.map((p, i) => {
              const img = p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url;
              return (
                <motion.a
                  key={p.media_id}
                  data-testid={`ig-post-${i}`}
                  href={p.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.76, 0, 0.24, 1] }}
                  className="group relative aspect-square overflow-hidden border border-[#6B5D4F]/10 bg-[#F7F1E6]"
                >
                  {img && (
                    <img
                      src={img}
                      alt={(p.caption || "Susan Grau on Instagram").slice(0, 120)}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  {p.media_product_type === "REELS" && (
                    <span className="absolute right-3 top-3 flex items-center gap-1.5 bg-[#6B5D4F]/70 px-3 py-1.5 font-body text-[0.55rem] font-medium uppercase tracking-[0.25em] text-[#FDFAF4] backdrop-blur-sm">
                      <Play size={10} /> Reel
                    </span>
                  )}
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-[#6B5D4F]/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <p className="line-clamp-2 p-5 font-body text-xs font-light leading-relaxed text-[#FDFAF4]">
                      {p.caption}
                    </p>
                  </div>
                </motion.a>
              );
            })}
          </div>
        ) : (
          <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
            {SOCIALS.reels.map((reel, i) => (
              <motion.div
                key={reel}
                data-testid={`ig-reel-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.76, 0, 0.24, 1] }}
                className="overflow-hidden border border-[#6B5D4F]/10 bg-[#F7F1E6]/60 p-3"
              >
                <iframe
                  title={`Susan Grau Instagram reel ${i + 1}`}
                  src={`${reel}embed`}
                  className="h-[520px] w-full"
                  frameBorder="0"
                  scrolling="no"
                  allowTransparency
                  loading="lazy"
                />
              </motion.div>
            ))}

            <motion.a
              data-testid="ig-photo-card"
              href={SOCIALS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
              className="group relative block min-h-[520px] overflow-hidden border border-[#C5A059]/50"
            >
              <img
                src={PHOTOS.standing}
                alt="Susan Grau"
                className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#6B5D4F]/85 via-transparent to-transparent" aria-hidden="true" />
              <div className="absolute inset-x-0 bottom-0 p-8">
                <Instagram size={22} strokeWidth={1.4} className="text-[#C5A059]" />
                <p className="mt-3 font-display text-2xl font-light text-[#FDFAF4]">Follow Susan on Instagram</p>
                <p className="mt-1 flex items-center gap-2 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#C5A059]">
                  @susangrauofficial
                  <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </p>
              </div>
            </motion.a>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-14 flex flex-wrap justify-center gap-4"
          data-testid="social-buttons"
        >
          {SOCIAL_BTNS.map(({ label, href, Icon }) => (
            <a
              key={label}
              data-testid={`social-btn-${label.toLowerCase()}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border border-[#6B5D4F]/15 bg-[#F7F1E6]/60 px-7 py-3.5 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F] transition-colors duration-500 hover:border-[#C5A059] hover:bg-[#6B5D4F] hover:text-[#FDFAF4]"
            >
              <Icon size={16} strokeWidth={1.5} className="text-[#C5A059]" />
              {label}
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
